/**
 * 초대 수락 — 최초 비밀번호 설정
 * ---------------------------------------------------------------------------
 * 초대 메일의 링크로 진입한다: /invite?token=<토큰>&t=<테넌트ID>
 *
 * 서버가 비밀번호 정책(기업별로 다름)을 최종 검증하므로,
 * 여기서는 공통 규칙만 미리 확인해 왕복을 줄인다.
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Result, Space, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';

const { Title, Text } = Typography;

export default function InvitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const token = params.get('token');
  const tenantId = params.get('t');

  if (!token || !tenantId) {
    return (
      <Centered>
        <Result
          status="error"
          title="초대 링크가 올바르지 않습니다"
          subTitle="메일에 포함된 링크 전체를 사용해 주세요. 문제가 계속되면 관리자에게 재발송을 요청하세요."
        />
      </Centered>
    );
  }

  if (done) {
    return (
      <Centered>
        <Result
          status="success"
          title="비밀번호가 설정되었습니다"
          subTitle={`${done} 계정으로 로그인할 수 있습니다.`}
          extra={
            <Button type="primary" onClick={() => navigate('/login', { replace: true })}>
              로그인하기
            </Button>
          }
        />
      </Centered>
    );
  }

  const submit = async (values: { password: string; confirmPassword: string }) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.post<{ email: string }>('/auth/invite/accept', {
        token,
        tenantId,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      setDone(data.email);
    } catch (err) {
      const e = err as NormalizedError;
      // 서버가 필드별 오류를 주면 그대로 보여준다(정책 위반 사유가 여러 개일 수 있다).
      const detail = e.details ? Object.values(e.details).flat().join(' ') : null;
      setError(detail ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Centered>
      <Card style={{ width: '100%', maxWidth: 440 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              비밀번호 설정
            </Title>
            <Text type="secondary">WORKnHub 사용을 시작하려면 비밀번호를 설정하세요.</Text>
          </div>

          {error && <Alert type="error" message={error} showIcon />}

          <Alert
            type="info"
            showIcon
            message="비밀번호 규칙"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>12자 이상</li>
                <li>영문과 숫자 포함</li>
                <li>같은 문자 3회 연속 사용 불가</li>
                <li>연속된 문자·키보드 배열 패턴 불가</li>
                <li>외부 유출 이력이 있는 비밀번호 불가</li>
              </ul>
            }
          />

          <Form layout="vertical" onFinish={submit} requiredMark={false}>
            <Form.Item
              name="password"
              label="새 비밀번호"
              rules={[
                { required: true, message: '비밀번호를 입력하세요' },
                { min: 12, message: '12자 이상이어야 합니다' },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} size="large" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="비밀번호 확인"
              dependencies={['password']}
              rules={[
                { required: true, message: '비밀번호를 다시 입력하세요' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || getFieldValue('password') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error('비밀번호가 일치하지 않습니다')),
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined />} size="large" autoComplete="new-password" />
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              설정 완료
            </Button>
          </Form>
        </Space>
      </Card>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 16 }}>
      {children}
    </div>
  );
}
